import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  Shield, Users, Building2, Heart, CheckCircle,
  ArrowRight, ChevronDown, Star, MapPin, Phone
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('staff');

  const stats = [
    { value: '500+', label: 'Verified Professionals' },
    { value: '50+', label: 'Partner Organizations' },
    { value: '20+', label: 'Cities Covered' },
    { value: '98%', label: 'Placement Satisfaction' },
  ];

  const howItWorks = {
    staff: [
      { step: '01', title: 'Register & Upload Documents', desc: 'Create your profile with Aadhaar, PAN, and professional credentials.' },
      { step: '02', title: 'Get Admin Verified', desc: 'Our team reviews and verifies your documents ensuring authenticity.' },
      { step: '03', title: 'Get Hired', desc: 'Approved companies discover and reach out to you directly through ShramSena.' },
    ],
    company: [
      { step: '01', title: 'Register Your Organization', desc: 'Sign up as a company or hospital. Submit your details for approval.' },
      { step: '02', title: 'Get Approved', desc: 'Our admin team verifies your organization and activates your account.' },
      { step: '03', title: 'Search & Hire', desc: 'Browse admin-verified healthcare professionals and send hire requests.' },
    ],
    family: [
      { step: '01', title: 'Describe Your Need', desc: 'Tell us what kind of healthcare support your family requires.' },
      { step: '02', title: 'Browse Trusted Providers', desc: 'Explore ShramSena-approved companies specializing in home care.' },
      { step: '03', title: 'Connect Directly', desc: 'Reach out to the provider that fits your need. They handle the rest.' },
    ],
  };

  const features = [
    { icon: Shield, title: 'Document Verified', desc: 'Every professional is verified with Aadhaar and PAN before listing.' },
    { icon: Users, title: 'Admin Screened', desc: 'Our ops team personally reviews each registration for quality assurance.' },
    { icon: MapPin, title: 'Pan-India Network', desc: 'Active across 20+ cities with growing presence in tier-2 markets.' },
    { icon: Star, title: 'Trusted Placements', desc: 'End-to-end placement support with post-hire accountability.' },
  ];

  const portals = [
    {
      icon: Users,
      type: 'Healthcare Professional',
      tagline: 'Find opportunities that match your expertise',
      bullets: ['ICU Nurses', 'Caregivers', 'Physiotherapists', 'Lab Technicians', 'Medical Assistants'],
      primaryAction: { label: 'Register as Staff', path: '/register' },
      secondaryAction: { label: 'Staff Login', path: '/login' },
      gradient: 'from-teal-500 to-cyan-600',
      bg: 'bg-teal-50',
      border: 'border-teal-200',
    },
    {
      icon: Building2,
      type: 'Hospital / Company',
      tagline: 'Hire verified healthcare staff at scale',
      bullets: ['Browse verified profiles', 'Shortlist candidates', 'Send hire requests', 'Admin-mediated placement'],
      primaryAction: { label: 'Register Organization', path: '/company/register' },
      secondaryAction: { label: 'Organization Login', path: '/company/login' },
      gradient: 'from-blue-600 to-indigo-700',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      featured: true,
    },
    {
      icon: Heart,
      type: 'Individual / Family',
      tagline: 'Find trusted care for your loved ones',
      bullets: ['Elder care', 'Post-surgery support', 'ICU at home', 'Daily health assistance'],
      primaryAction: { label: 'Find a Provider', path: '/providers' },
      secondaryAction: null,
      gradient: 'from-rose-500 to-pink-600',
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      comingSoon: true,
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* ── NAVBAR ────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-10 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <a href="#how-it-works" className="hover:text-teal-600 transition">How It Works</a>
            <a href="#portals" className="hover:text-teal-600 transition">Portals</a>
            <a href="#trust" className="hover:text-teal-600 transition">Why ShramSena</a>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition px-4 py-2">
              Staff Login
            </button>
            <button
              onClick={() => navigate('/company/login')}
              className="text-sm font-semibold bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition">
              Organization Login
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #14b8a6 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, #0ea5e9 0%, transparent 50%)`
          }} />
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 rounded-full px-4 py-1.5 mb-6">
              <Shield className="h-3.5 w-3.5 text-teal-400" />
              <span className="text-teal-300 text-xs font-semibold tracking-wide uppercase">
                India's Trusted Healthcare Staffing Platform
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              The Right Healthcare
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">
                Professional, Every Time.
              </span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl">
              ShramSena connects verified healthcare professionals with hospitals,
              care organizations, and families across India. Every placement
              is backed by document verification and admin oversight.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/company/register')}
                className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-7 py-3.5 rounded-xl transition shadow-lg shadow-teal-500/30">
                Hire Healthcare Staff <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/register')}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-7 py-3.5 rounded-xl transition backdrop-blur">
                Find Healthcare Jobs <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 animate-bounce">
          <ChevronDown className="h-6 w-6" />
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────────────── */}
      <section className="bg-teal-600 py-10 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-3xl font-bold text-white">{s.value}</p>
              <p className="text-teal-100 text-sm mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-sm uppercase tracking-widest mb-2">
              Simple Process
            </p>
            <h2 className="text-4xl font-bold text-slate-900">How ShramSena Works</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">
              Whether you're a professional, organization, or family — we've built
              a clear path for each of you.
            </p>
          </div>

          {/* Tab selector */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
              {[
                { key: 'staff', label: 'Healthcare Staff', icon: Users },
                { key: 'company', label: 'Organization', icon: Building2 },
                { key: 'family', label: 'Individual / Family', icon: Heart },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition
                    ${activeTab === key
                      ? 'bg-teal-600 text-white shadow'
                      : 'text-gray-500 hover:text-gray-800'}`}>
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howItWorks[activeTab].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm relative">
                <span className="text-6xl font-black text-gray-100 absolute top-4 right-6 leading-none">
                  {item.step}
                </span>
                <div className="relative">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                    <CheckCircle className="h-5 w-5 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PORTALS ───────────────────────────────────────────────── */}
      <section id="portals" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-sm uppercase tracking-widest mb-2">
              Choose Your Portal
            </p>
            <h2 className="text-4xl font-bold text-slate-900">Who Are You?</h2>
            <p className="text-gray-500 mt-3">
              ShramSena serves three distinct groups — each with a dedicated experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {portals.map((portal) => {
              const Icon = portal.icon;
              return (
                <div
                  key={portal.type}
                  className={`relative rounded-2xl border-2 ${portal.border} ${portal.bg}
                    p-8 flex flex-col
                    ${portal.featured ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>

                  {portal.featured && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  {portal.comingSoon && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-rose-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        COMING SOON
                      </span>
                    </div>
                  )}

                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${portal.gradient}
                    flex items-center justify-center mb-5 shadow-lg`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-1">{portal.type}</h3>
                  <p className="text-gray-500 text-sm mb-5">{portal.tagline}</p>

                  <ul className="space-y-2 mb-8 flex-1">
                    {portal.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-teal-500 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-3">
                    <button
                      onClick={() => !portal.comingSoon && navigate(portal.primaryAction.path)}
                      className={`w-full py-3 rounded-xl font-semibold text-sm transition
                        ${portal.comingSoon
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : `bg-gradient-to-r ${portal.gradient} text-white hover:opacity-90 shadow-md`}`}>
                      {portal.comingSoon ? 'Coming in Phase 3' : portal.primaryAction.label}
                    </button>
                    {portal.secondaryAction && (
                      <button
                        onClick={() => navigate(portal.secondaryAction.path)}
                        className="w-full py-2.5 rounded-xl border border-gray-300 text-gray-600
                          font-semibold text-sm hover:bg-white transition">
                        {portal.secondaryAction.label}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TRUST / WHY SHRAMSENA ─────────────────────────────────── */}
      <section id="trust" className="py-20 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-teal-400 font-semibold text-sm uppercase tracking-widest mb-2">
              Our Commitment
            </p>
            <h2 className="text-4xl font-bold text-white">Why Organizations Trust ShramSena</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-white font-bold mb-2">{f.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Healthcare Staffing?
          </h2>
          <p className="text-teal-100 mb-8 text-lg">
            Join India's growing network of verified healthcare professionals and trusted organizations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/company/register')}
              className="bg-white text-teal-700 font-bold px-8 py-3.5 rounded-xl hover:bg-teal-50 transition shadow-lg">
              Register Your Organization
            </button>
            <button
              onClick={() => navigate('/register')}
              className="bg-teal-800/40 border border-white/30 text-white font-bold px-8 py-3.5 rounded-xl hover:bg-teal-800/60 transition">
              Join as Healthcare Staff
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────── */}
      <footer className="bg-slate-950 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-white.png" alt="ShramSena" className="h-8 w-auto opacity-70" />
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Phone className="h-3.5 w-3.5" />
            <span>For support: support@shramsena.in</span>
          </div>
          <p className="text-slate-600 text-sm">
            © {new Date().getFullYear()} ShramSena™ — Your Trusted Healthcare Staffing Partner
          </p>
        </div>
      </footer>
    </div>
  );
}
